import { pillarArticlesA } from '@/mocks/pillarArticlesA';
import { pillarArticlesB } from '@/mocks/pillarArticlesB';
import {
  SITE_ORIGIN,
  SITE_NAME,
  SITE_DESCRIPTION,
  LOGO_URL,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from './site';

type Localized = { en: string; pt: string; de: string };

function pick(text: Localized, lang: string): string {
  if (lang === 'pt') return text.pt;
  if (lang === 'de') return text.de;
  return text.en;
}

const allArticles = [...pillarArticlesA, ...pillarArticlesB];

const LANG_RE = /^\/(en|pt|de)(?:\/|$)/;

function detectLang(pathname: string): string {
  const match = pathname.match(LANG_RE);
  return match ? match[1] : 'en';
}

const HOME_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy buying guide, CSSBuy sellers, CSSBuy QC';

const ARTICLE_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy guide, CSSBuy products, CSSBuy sellers, CSSBuy shipping';

const GENERIC_SEO: Record<string, { title: string; description: string }> = {
  review: {
    title: 'CSSBuy Review 2026',
    description:
      'An in-depth review of CSSBuy covering features, pros, cons, safety, and user experience.',
  },
  shipping: {
    title: 'CSSBuy Shipping Cost Guide',
    description:
      'Everything about CSSBuy shipping methods, costs, weight calculations, and tips to save money.',
  },
  qc: {
    title: 'CSSBuy QC Guide',
    description:
      'Learn how to read QC photos, what to check, and how to handle quality issues.',
  },
  warehouse: {
    title: 'CSSBuy Warehouse Guide',
    description:
      'Understanding CSSBuy warehouse storage, processing times, and workflow.',
  },
  safety: {
    title: 'CSSBuy Safety Center',
    description:
      'Safety guidelines for using CSSBuy including avoiding scams, fake sites, and payment security.',
  },
  about: {
    title: 'About CSSBuy Spreadsheet',
    description:
      'Learn about our independent resource hub and mission to help shoppers make informed decisions.',
  },
  legit: {
    title: 'Is CSSBuy Legit?',
    description:
      'A comprehensive analysis of CSSBuy legitimacy, safety, and trustworthiness.',
  },
  sellers: {
    title: 'CSSBuy Sellers Guide',
    description:
      'How to find, evaluate, and choose trusted sellers when using CSSBuy.',
  },
  coupon: {
    title: 'CSSBuy Coupon Guide',
    description:
      'Everything about CSSBuy coupons, discount codes, and how to save money.',
  },
  discount: {
    title: 'CSSBuy Discount Code Guide',
    description:
      'Guide to finding and using CSSBuy discount codes and promotional offers.',
  },
  comparisons: {
    title: 'CSSBuy Comparisons',
    description: 'Compare CSSBuy with alternatives like Superbuy and other agents.',
  },
  'editorial-policy': {
    title: 'Editorial Policy',
    description:
      'How we research, write, and maintain the CSSBuy Spreadsheet guides and reviews.',
  },
  disclaimer: {
    title: 'Disclaimer',
    description:
      'Important information about the nature of this independent resource hub.',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'How we handle visitor information and privacy on this website.',
  },
  terms: {
    title: 'Terms of Service',
    description: 'The terms and conditions for using this website and its resources.',
  },
};

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType: string;
  ogImage: string;
  jsonLd: Record<string, unknown>[];
}

function websiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
    description: SITE_DESCRIPTION,
    inLanguage: ['en', 'pt', 'de'],
  };
}

function organizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
    logo: LOGO_URL,
  };
}

function webPageJsonLd(
  url: string,
  name: string,
  description: string
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: `${SITE_ORIGIN}/` },
  };
}

function articleJsonLd(
  url: string,
  headline: string,
  description: string,
  image?: string
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    url,
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: '2026-01-15',
    dateModified: '2026-08-27',
    inLanguage: ['en', 'pt', 'de'],
  };
  if (image) data.image = image;
  return data;
}

export function resolveSeo(pathname: string): SeoData {
  const lang = detectLang(pathname);
  const canonical = absoluteUrl(pathname === '/' ? '/' : pathname);

  // Home
  if (pathname === '/' || /^\/(en|pt|de)\/?$/.test(pathname)) {
    return {
      title: `${SITE_NAME} | Products, Sellers & Buying Guides 2026`,
      description: SITE_DESCRIPTION,
      keywords: HOME_KEYWORDS,
      canonical: absoluteUrl('/'),
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [websiteJsonLd(), organizationJsonLd()],
    };
  }

  // Products
  if (/^\/(en|pt|de)\/products$/.test(pathname)) {
    const description =
      'Browse all curated products from the CSSBuy Spreadsheet, including shoes, clothing, accessories, and more. Click any item to view details and purchase.';
    return {
      title: `All Products | ${SITE_NAME}`,
      description,
      keywords:
        'CSSBuy products, CSSBuy spreadsheet, CSSBuy shoes, CSSBuy clothing, CSSBuy accessories',
      canonical,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [webPageJsonLd(canonical, 'All Products', description)],
    };
  }

  // Main pillar article
  if (/^\/(en|pt|de)\/cssbuy-spreadsheet$/.test(pathname)) {
    const description =
      'The complete CSSBuy Spreadsheet guide covering products, sellers, QC, shipping, and safety for 2026.';
    return {
      title: `CSSBuy Spreadsheet Guide | ${SITE_NAME}`,
      description,
      keywords: ARTICLE_KEYWORDS,
      canonical,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [articleJsonLd(canonical, 'CSSBuy Spreadsheet Guide', description)],
    };
  }

  // Pillar article by slug
  const articleMatch = pathname.match(/^\/(en|pt|de)\/articles\/([^/]+)$/);
  if (articleMatch) {
    const slug = articleMatch[2];
    const article = allArticles.find((a) => a.slug === slug);
    if (article) {
      const title = pick(article.title, lang);
      const description = pick(article.summary, lang);
      return {
        title: `${title} | ${SITE_NAME}`,
        description,
        keywords: ARTICLE_KEYWORDS,
        canonical,
        ogType: 'article',
        ogImage: article.image,
        jsonLd: [articleJsonLd(canonical, title, description, article.image)],
      };
    }
  }

  // Generic content pages
  const genericMatch = pathname.match(/^\/(en|pt|de)\/([^/]+)$/);
  if (genericMatch) {
    const page = genericMatch[2];
    const seo = GENERIC_SEO[page];
    if (seo) {
      return {
        title: `${seo.title} | ${SITE_NAME}`,
        description: seo.description,
        keywords: 'CSSBuy Spreadsheet, CSSBuy guide, CSSBuy',
        canonical,
        ogType: 'website',
        ogImage: DEFAULT_OG_IMAGE,
        jsonLd: [webPageJsonLd(canonical, seo.title, seo.description)],
      };
    }
  }

  // Fallback (e.g. NotFound)
  return {
    title: SITE_NAME,
    description: SITE_DESCRIPTION,
    keywords: HOME_KEYWORDS,
    canonical,
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    jsonLd: [],
  };
}